import React from 'react';
import { ShieldAlert } from 'lucide-react';

const CardIcon = ({ color, className = "w-3 h-4" }) => (
  <div className={`${className} rounded-sm shadow ${color === 'yellow' ? 'bg-[#FFD400]' : color === 'red' ? 'bg-[#FF3B30]' : 'bg-[#00D26A]'}`} />
);

const eventMeta = {
  goal: { label: 'Goal', icon: <div className="w-3 h-3 bg-white rounded-full border border-gray-400" /> },
  yellow: { label: 'Yellow Card', icon: <CardIcon color="yellow" /> },
  red: { label: 'Red Card', icon: <CardIcon color="red" /> },
  sin_bin: { label: 'Sin Bin', icon: <CardIcon color="success" /> },
  stoppage: { label: 'Stoppage', icon: <div className="w-3 h-3 rounded-full bg-gray-500" /> },
  notes: { label: 'Note', icon: <div className="w-3 h-3 rounded-full bg-[#00BFFF]" /> },
};

const minuteNum = (m) => parseInt(String(m).replace(/[^0-9]/g, ''), 10) || 0;

export default function MatchReport({ match }) {
  if (!match) return null;
  const { config, matchState } = match;
  const events = [...(matchState.events || [])]
    .filter(e => e.type !== 'stoppage')
    .sort((a, b) => (a.period - b.period) || (minuteNum(a.minute) - minuteNum(b.minute)));

  return (
    <div data-testid="match-report" className="w-full text-white">
      {/* Brand header */}
      <div className="flex items-center justify-center gap-2 mb-6">
        <ShieldAlert className="w-5 h-5 text-[#00BFFF]" />
        <span className="text-sm font-black tracking-[0.3em] uppercase">Tournevo</span>
      </div>

      {/* Meta */}
      <p className="text-center text-[11px] text-gray-500 uppercase tracking-widest mb-4">
        {match.date} • {config.competition || 'Friendly'}{config.venue ? ` • ${config.venue}` : ''}
      </p>

      {/* Scoreboard */}
      <div className="grid grid-cols-3 items-center bg-[#1C1C1E] border border-gray-800 rounded-2xl p-5 mb-6">
        <div className="text-center">
          <p className="text-xs text-gray-400 truncate mb-2">{config.homeTeam}</p>
          <p className="text-4xl font-black text-[#00BFFF]">{matchState.homeScore}</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">Full Time</p>
          <p className="text-2xl font-black text-gray-600">-</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-400 truncate mb-2">{config.awayTeam}</p>
          <p className="text-4xl font-black text-[#FF3B30]">{matchState.awayScore}</p>
        </div>
      </div>

      {/* Timeline */}
      <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mb-3">Match Events</p>
      <div className="space-y-2">
        {events.length === 0 ? (
          <p className="text-center text-gray-600 text-sm py-6">No events recorded.</p>
        ) : (
          events.map(e => {
            const meta = eventMeta[e.type] || { label: e.type, icon: null };
            const isHome = e.team === 'home';
            return (
              <div key={e.id} className="flex items-center bg-[#1C1C1E] border border-gray-800 rounded-xl px-3 py-2.5">
                <span className="text-xs font-mono font-bold text-[#FFD400] w-10 shrink-0">{e.minute}</span>
                <div className="w-5 flex justify-center shrink-0">{meta.icon}</div>
                <div className="flex-1 ml-2 min-w-0">
                  <p className="text-sm font-bold truncate">
                    {meta.label}{e.player ? ` • #${e.player}` : ''}
                  </p>
                  {e.reason ? <p className="text-[11px] text-gray-500 truncate">{e.reason}</p> : null}
                </div>
                {(e.type !== 'notes') && (
                  <span className={`text-[10px] font-bold uppercase tracking-wider shrink-0 ${isHome ? 'text-[#00BFFF]' : 'text-[#FF3B30]'}`}>
                    {isHome ? config.homeTeam : config.awayTeam}
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>

      <p className="text-center text-[10px] text-gray-600 mt-8 tracking-wider">
        Officiated with Tournevo
      </p>
    </div>
  );
}
